import React, { useState, useEffect, useRef } from 'react';
import { useAuthStore } from '../store/authStore';
import { useToast } from '../contexts/ToastContext';
import LoadingSpinner from '../components/LoadingSpinner';
import { usePageTitle } from '../hooks/usePageTitle';

const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const ProfilePage: React.FC = () => {
  usePageTitle('Profile Settings');
  const { user, updateProfile, loading } = useAuthStore();
  const { showSuccess, showError } = useToast();
  
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    avatar_color: '#3B82F6'
  });

  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageSize, setImageSize] = useState<number>(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [passwordForm, setPasswordForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [showPasswords, setShowPasswords] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState('');

  // Color options for avatar
  const avatarColors = [
    '#3B82F6', '#EF4444', '#10B981', '#F59E0B', 
    '#8B5CF6', '#06B6D4', '#84CC16', '#F97316',
    '#EC4899', '#6366F1', '#14B8A6', '#F43F5E',
    '#6B7280', '#059669', '#DC2626', '#7C3AED'
  ];

  // Initialize form with user data
  useEffect(() => {
    if (user) {
      setFormData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        avatar_color: user.avatar_color || '#3B82F6'
      });
      // Only update preview if no selected image (to avoid overwriting user's selection)
      if (!selectedImage) {
        if (user.avatar_image_url) {
          setImagePreview(user.avatar_image_url);
        } else {
          setImagePreview(null);
        }
      }
    }
  }, [user, selectedImage]);

  // Check for changes
  useEffect(() => {
    if (user) {
      const hasChanged = 
        formData.first_name !== (user.first_name || '') ||
        formData.last_name !== (user.last_name || '') ||
        formData.avatar_color !== user.avatar_color ||
        selectedImage !== null;
      setHasChanges(hasChanged);
    }
  }, [formData, selectedImage, user]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleColorChange = (color: string) => {
    setFormData(prev => ({
      ...prev,
      avatar_color: color
    }));
  };

  const generateRandomColor = () => {
    const randomColor = avatarColors[Math.floor(Math.random() * avatarColors.length)];
    handleColorChange(randomColor);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check file type
    if (!file.type.startsWith('image/')) {
      showError('Invalid File Type', 'Please select a valid image file (JPG, PNG, GIF).');
      return;
    }

    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      showError('File Too Large', `Image size must be less than ${formatFileSize(MAX_FILE_SIZE)}`);
      return;
    }

    setSelectedImage(file);
    setImageSize(file.size);

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveImage = () => {
    setSelectedImage(null);
    setImagePreview(user?.avatar_image_url || null);
    setImageSize(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!hasChanges || isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      // If image is selected, use FormData
      if (selectedImage) {
        const formDataToSend = new FormData();
        formDataToSend.append('first_name', formData.first_name);
        formDataToSend.append('last_name', formData.last_name);
        formDataToSend.append('avatar_color', formData.avatar_color);
        formDataToSend.append('avatar_image', selectedImage);
        
        await updateProfile(formDataToSend);
      } else {
        // Regular JSON update
        await updateProfile(formData);
      }
      
      // Get updated user from store
      const updatedUser = useAuthStore.getState().user;
      if (updatedUser?.avatar_image_url) {
        setImagePreview(updatedUser.avatar_image_url);
      } else if (!selectedImage) {
        setImagePreview(null);
      }
      
      showSuccess('Profile Updated!', 'Your profile has been updated successfully.');
      setHasChanges(false);
      setSelectedImage(null);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update profile. Please try again.';
      showError('Update Failed', errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError('');

    if (passwordForm.new_password !== passwordForm.confirm_password) {
      setPasswordError('New passwords do not match.');
      return;
    }

    setPasswordLoading(true);
    try {
      const { tokens } = useAuthStore.getState();
      const res = await fetch(`${API_BASE}/auth/change-password/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(tokens?.access ? { 'Authorization': `Bearer ${tokens.access}` } : {}),
        },
        credentials: 'include',
        body: JSON.stringify({
          current_password: passwordForm.current_password,
          new_password: passwordForm.new_password,
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(data.error || data.detail || 'Failed to change password.');
      }

      showSuccess('Password Changed', 'Your password has been updated successfully.');
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      setPasswordError(err instanceof Error ? err.message : 'Failed to change password.');
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleCancel = () => {
    if (user) {
      setFormData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        avatar_color: user.avatar_color || '#3B82F6'
      });
      setSelectedImage(null);
      setImagePreview(user.avatar_image_url || null);
      setImageSize(0);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  if (loading && !user) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-slate-900">User not found</h2>
          <p className="text-slate-500">Please try logging in again.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Avatar Section */}
          <div className="lg:col-span-1">
            <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-6 sticky top-8">
              <h3 className="text-lg font-semibold text-slate-900 mb-6">
                Profile
              </h3>
              
              {/* Avatar Preview */}
              <div className="flex flex-col items-center mb-6">
                <div className="relative mb-4">
                  {imagePreview ? (
                    <div className="relative">
                      <img
                        src={imagePreview}
                        alt="Avatar preview"
                        className="w-32 h-32 rounded-full object-cover border-4 border-slate-100 shadow-lg"
                      />
                      {selectedImage && (
                        <button
                          type="button"
                          onClick={handleRemoveImage}
                          className="absolute -top-1 -right-1 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-all shadow-lg hover:scale-110"
                          title="Remove image"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      )}
                    </div>
                  ) : (
                    <div 
                      className="w-32 h-32 rounded-full flex items-center justify-center text-white text-4xl font-bold border-4 border-slate-100 shadow-lg"
                      style={{ backgroundColor: formData.avatar_color }}
                    >
                      {formData.first_name 
                        ? `${formData.first_name[0]}${formData.last_name?.[0] || ''}`.toUpperCase()
                        : user.email 
                        ? user.email[0].toUpperCase() 
                        : 'U'}
                    </div>
                  )}
                </div>

                {/* Upload Button */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif"
                  onChange={handleImageSelect}
                  className="hidden"
                  id="avatar-upload"
                />
                <label
                  htmlFor="avatar-upload"
                  className="w-full px-4 py-2.5 bg-orange-500 hover:bg-orange-600 text-white font-medium rounded-xl cursor-pointer transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  {selectedImage ? 'Change Image' : 'Upload Image'}
                </label>

                {/* File Info */}
                {selectedImage && (
                  <div className="mt-4 w-full text-center">
                    <p className="text-sm font-medium text-slate-700 mb-1 truncate">{selectedImage.name}</p>
                    <p className="text-xs text-slate-500">
                      Size: <span className={imageSize > MAX_FILE_SIZE ? 'text-red-600 font-medium' : 'text-slate-600'}>
                        {formatFileSize(imageSize)}
                      </span>
                      {' '}/ Max: {formatFileSize(MAX_FILE_SIZE)}
                    </p>
                  </div>
                )}
                
                {!selectedImage && user.avatar_image_url && (
                  <p className="mt-2 text-xs text-slate-400 text-center">Current image will be kept</p>
                )}
              </div>

              {/* Avatar Color Picker */}
              <div className="border-t border-slate-200 pt-6">
                <label className="block text-sm font-medium text-slate-700 mb-3">
                  Avatar Color
                </label>
                <div className="grid grid-cols-4 gap-2 mb-3">
                  {avatarColors.map((color) => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => handleColorChange(color)}
                      className={`w-10 h-10 rounded-lg border-2 transition-all hover:scale-110 ${
                        formData.avatar_color === color 
                          ? 'border-slate-900 scale-110 shadow-md' 
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                      style={{ backgroundColor: color }}
                      title={color}
                    />
                  ))}
                </div>
                <button
                  type="button"
                  onClick={generateRandomColor}
                  className="w-full text-sm text-orange-500 hover:text-orange-600 font-medium py-2 hover:bg-orange-50 rounded-lg transition-colors"
                >
                  🎲 Random Color
                </button>
                <p className="mt-2 text-xs text-slate-400">
                  Used when no profile image is uploaded
                </p>
              </div>
            </div>
          </div>

          {/* Right Column - Form */}
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Personal Information Card */}
              <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-6">
                <h3 className="text-lg font-semibold text-slate-900 mb-6">
                  Personal Information
                </h3>
                
                <div className="space-y-5">
                  {/* Name Fields */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="first_name" className="block text-sm font-medium text-slate-700 mb-2">
                        First Name
                      </label>
                      <input
                        type="text"
                        id="first_name"
                        name="first_name"
                        value={formData.first_name}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                        placeholder="Enter your first name"
                      />
                    </div>

                    <div>
                      <label htmlFor="last_name" className="block text-sm font-medium text-slate-700 mb-2">
                        Last Name
                      </label>
                      <input
                        type="text"
                        id="last_name"
                        name="last_name"
                        value={formData.last_name}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                        placeholder="Enter your last name"
                      />
                    </div>
                  </div>

                  {/* Email (Read-only) */}
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-2">
                      Email Address
                    </label>
                    <input
                      type="email"
                      id="email"
                      value={user.email || ''}
                      disabled
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-500 cursor-not-allowed"
                    />
                    <p className="mt-2 text-xs text-slate-400">
                      Email cannot be changed. Contact support if you need to update your email address.
                    </p>
                  </div>
                </div>
              </div>

              {/* Account Information Card */}
              <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-6">
                <h3 className="text-lg font-semibold text-slate-900 mb-6">
                  Account Information
                </h3>
                
                <div className="space-y-4">
                  <div className="flex justify-between items-center py-3 border-b border-slate-200">
                    <div>
                      <dt className="text-sm font-medium text-slate-900">
                        Account Type
                      </dt>
                      <dd className="text-sm text-slate-500 mt-1">
                        {user.is_anonymous_user ? 'Anonymous User' : 'Registered User'}
                      </dd>
                    </div>
                    {!user.is_anonymous_user && (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-emerald-50 text-emerald-600 border border-emerald-200">
                        ✓ Verified
                      </span>
                    )}
                  </div>

                  <div className="flex justify-between items-center py-3 border-b border-slate-200">
                    <div>
                      <dt className="text-sm font-medium text-slate-900">
                        Member Since
                      </dt>
                      <dd className="text-sm text-slate-500 mt-1">
                        {user.created_at ? new Date(user.created_at).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        }) : 'Unknown'}
                      </dd>
                    </div>
                  </div>

                  <div className="flex justify-between items-center py-3">
                    <div>
                      <dt className="text-sm font-medium text-slate-900">
                        User ID
                      </dt>
                      <dd className="text-sm text-slate-500 font-mono mt-1">
                        {user.id}
                      </dd>
                    </div>
                  </div>
                </div>
              </div>

              {/* Security Card */}
              {!user.is_anonymous_user && (
                <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-6">
                  <h3 className="text-lg font-semibold text-slate-900 mb-6">Security</h3>
                  <form onSubmit={handleChangePassword} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Current Password</label>
                      <input
                        type={showPasswords ? 'text' : 'password'}
                        value={passwordForm.current_password}
                        onChange={e => { setPasswordForm(p => ({ ...p, current_password: e.target.value })); setPasswordError(''); }}
                        required
                        placeholder="Enter current password"
                        className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">New Password</label>
                        <input
                          type={showPasswords ? 'text' : 'password'}
                          value={passwordForm.new_password}
                          onChange={e => { setPasswordForm(p => ({ ...p, new_password: e.target.value })); setPasswordError(''); }}
                          required
                          minLength={8}
                          placeholder="Min. 8 characters"
                          className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Confirm New Password</label>
                        <input
                          type={showPasswords ? 'text' : 'password'}
                          value={passwordForm.confirm_password}
                          onChange={e => { setPasswordForm(p => ({ ...p, confirm_password: e.target.value })); setPasswordError(''); }}
                          required
                          placeholder="Repeat new password"
                          className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                        />
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="show-passwords"
                        checked={showPasswords}
                        onChange={e => setShowPasswords(e.target.checked)}
                        className="rounded border-slate-300 text-orange-500 focus:ring-orange-500"
                      />
                      <label htmlFor="show-passwords" className="text-sm text-slate-500 cursor-pointer">Show passwords</label>
                    </div>

                    {passwordError && (
                      <div className="p-3 bg-red-50 border border-red-200 rounded-xl">
                        <p className="text-sm text-red-600">{passwordError}</p>
                      </div>
                    )}

                    <div className="flex justify-end">
                      <button
                        type="submit"
                        disabled={passwordLoading || !passwordForm.current_password || !passwordForm.new_password || !passwordForm.confirm_password}
                        className="px-6 py-3 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 shadow-md hover:shadow-lg"
                      >
                        {passwordLoading ? <><LoadingSpinner size="small" /> Updating...</> : 'Update Password'}
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={handleCancel}
                  disabled={!hasChanges || isSubmitting}
                  className="px-6 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!hasChanges || isSubmitting}
                  className="px-6 py-3 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 shadow-md hover:shadow-lg"
                >
                  {isSubmitting ? (
                    <>
                      <LoadingSpinner size="small" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
